import { Fix64 } from "../maths/fix64";
import { Enemy, Tower, Troop } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import type { PetSystem } from "./petSystem";
import { withinBark } from "./troopRules";

/**
 * A Pets v3 passive (docs/PETS.md "Pets v3": Night Eyes, Kindle, Shell Wall, Mend, Shiny Find), always on around the
 * flag. Shell Wall rides on the troops' blow (TroopSystem.guard, troopRules.ts troopBlow); Mend heals here on its
 * cadence; the rest are the hooks combat calls (`petDamageTaken`, `petKillPoints`) when the run's pet has a v3 kit.
 */
export function tickAura(sim: LevelSim, pet: PetSystem): void {
  const a = pet.rule.aura;
  if (!a) return;
  if (pet.guardBuff !== null) sim.troops.guard = pet.guardBuff;
  if (a.healEveryTicks <= 0) return;
  if (pet.healTimer > 0) pet.healTimer--;
  if (pet.healTimer > 0) return;
  const t = mostHurt(sim, pet);
  if (t === null) return;                                 // nobody hurt: Mend waits, ready
  pet.healTimer = a.healEveryTicks;
  const amount = Fix64.min(t.maxHp.mulInt(a.healPercent).divInt(PERCENT), t.maxHp.sub(t.hp));
  t.hp = t.hp.add(amount);
  sim.events.add(SimEventKind.PetMended, t.id, amount.floorToInt());
}

const PERCENT = 100;

/** The fortress troop within the aura's reach with the smallest share of its health (ties to the lower slot), or null when none is hurt. */
function mostHurt(sim: LevelSim, pet: PetSystem): Troop | null {
  const r = sim.troops.rally, buff = pet.reachBuff!;
  let best: Troop | null = null, bestShare = Fix64.One;
  for (const t of sim.troops.slots) {
    if (!t.alive || !t.hp.lt(t.maxHp) || !withinBark(buff, r.x, r.y, t.x, t.y)) continue;
    const share = t.hp.div(t.maxHp);
    if (best === null || share.lt(bestShare)) { best = t; bestShare = share; }
  }
  return best;
}

/** Whether `e` stands within the aura's reach of the flag's cell. */
function nearFlag(sim: LevelSim, pet: PetSystem, e: Enemy): boolean {
  const r = sim.troops.rally;
  return withinBark(pet.reachBuff!, r.x, r.y, e.x, e.y);
}

/**
 * The damage `e` takes after the pets' cuts (combat's applyDamage, after the Hex curse): a mark's extra (Lantern Flare,
 * Glint), Kindle's on a burning raider (the towers' burns or the pet's), Night Eyes' on a flyer a tower hits.
 */
export function petDamageTaken(sim: LevelSim, e: Enemy, source: Tower | null, damage: Fix64): Fix64 {
  const a = sim.petSystem!.rule.aura;
  let d = damage;
  if (e.markTicks > 0 && e.markTaken.isPositive()) d = d.mul(Fix64.One.add(e.markTaken));
  if (a) {
    if (a.burningTakenPercent > 0 && (e.burnTicksLeft > 0 || e.petBurnTicks > 0)) d = d.mul(Fix64.fromInt(PERCENT + a.burningTakenPercent)).divInt(PERCENT);
    if (a.towerAirPercent > 0 && source !== null && e.isAir) d = d.mul(Fix64.fromInt(PERCENT + a.towerAirPercent)).divInt(PERCENT);
  }
  return d;
}

/** The kill points of `e` after the pets' bonuses: a mark's (Glint), Shiny Find's within reach of the flag. Points only: never gold or a chest. */
export function petKillPoints(sim: LevelSim, e: Enemy, points: number): number {
  const pet = sim.petSystem!, a = pet.rule.aura;
  let bonus = e.markTicks > 0 ? e.markPointsPercent : 0;
  if (a && a.killPointsPercent > 0 && nearFlag(sim, pet, e)) bonus += a.killPointsPercent;
  // whole numbers throughout (points and percents are integers), so +15 % of 100 is 115, never 114.99…
  return bonus > 0 ? Math.floor((points * (PERCENT + bonus)) / PERCENT) : points;
}
